import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { StatusBadge, SubmissionStatusBadge } from '../components/StatusBadge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Download, Loader2, FolderKanban, Users, Calendar, Filter, ListTodo } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate list of months for specific month selector
const generateMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = subMonths(now, i);
    options.push({
      value: format(date, 'yyyy-MM'),
      label: format(date, 'MMMM yyyy'),
    });
  }
  return options;
};

export default function ReportsPage() {
  const [projectReport, setProjectReport] = useState([]);
  const [userReport, setUserReport] = useState([]);
  const [taskReport, setTaskReport] = useState([]);
  const [taskView, setTaskView] = useState('by-project'); // 'by-project' | 'by-client'
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [timePeriod, setTimePeriod] = useState('all');
  const [specificMonth, setSpecificMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  const monthOptions = generateMonthOptions();

  // Calculate date range based on selected filter
  const getDateRange = useCallback(() => {
    const now = new Date();
    
    switch (timePeriod) {
      case 'this_month':
        return {
          start_date: format(startOfMonth(now), 'yyyy-MM-dd'),
          end_date: format(endOfMonth(now), 'yyyy-MM-dd'),
        };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return {
          start_date: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          end_date: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        };
      case 'specific_month':
        const [year, month] = specificMonth.split('-');
        const specificDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        return {
          start_date: format(startOfMonth(specificDate), 'yyyy-MM-dd'),
          end_date: format(endOfMonth(specificDate), 'yyyy-MM-dd'),
        };
      case 'custom':
        return {
          start_date: customStartDate || undefined,
          end_date: customEndDate || undefined,
        };
      case 'all':
      default:
        return {
          start_date: undefined,
          end_date: undefined,
        };
    }
  }, [timePeriod, specificMonth, customStartDate, customEndDate]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      const params = {};
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;

      const [projectRes, userRes, taskRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/projects`, { params }),
        axios.get(`${API_URL}/api/reports/users`, { params }),
        axios.get(`${API_URL}/api/reports/tasks`, { params }),
      ]);
      setProjectReport(projectRes.data);
      setUserReport(userRes.data);
      setTaskReport(taskRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Aggregate task hours by client across all projects
  const clientTaskRollup = useMemo(() => {
    const byClient = {};
    for (const project of taskReport) {
      const cid = project.client_id || '__no_client__';
      const cname = project.client_name || 'No Client';
      if (!byClient[cid]) byClient[cid] = { client_id: cid, client_name: cname, tasks: {}, project_count: 0 };
      byClient[cid].project_count += 1;
      for (const task of project.tasks) {
        if (!byClient[cid].tasks[task.task_id]) {
          byClient[cid].tasks[task.task_id] = { ...task, total_hours: 0, entry_count: 0 };
        }
        byClient[cid].tasks[task.task_id].total_hours += task.total_hours;
        byClient[cid].tasks[task.task_id].entry_count += task.entry_count;
      }
    }
    return Object.values(byClient).map(c => ({
      ...c,
      tasks: Object.values(c.tasks).sort((a, b) => b.total_hours - a.total_hours),
    })).sort((a, b) => a.client_name.localeCompare(b.client_name));
  }, [taskReport]);

  // Get display label for current filter
  const getFilterLabel = () => {
    switch (timePeriod) {
      case 'this_month':
        return format(new Date(), 'MMMM yyyy');
      case 'last_month':
        return format(subMonths(new Date(), 1), 'MMMM yyyy');
      case 'specific_month':
        const [year, month] = specificMonth.split('-');
        return format(new Date(parseInt(year), parseInt(month) - 1, 1), 'MMMM yyyy');
      case 'custom':
        if (customStartDate && customEndDate) {
          return `${customStartDate} to ${customEndDate}`;
        } else if (customStartDate) {
          return `From ${customStartDate}`;
        } else if (customEndDate) {
          return `Until ${customEndDate}`;
        }
        return 'Custom Range';
      case 'all':
      default:
        return 'All Time';
    }
  };

  const downloadProjectCSV = async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const params = {};
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;
      
      const response = await axios.get(`${API_URL}/api/reports/export/projects`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `project_report_${getFilterLabel().replace(/ /g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Project report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const downloadUserCSV = async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const params = {};
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;
      
      const response = await axios.get(`${API_URL}/api/reports/export/users`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `user_report_${getFilterLabel().replace(/ /g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('User report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const downloadTaskCSV = async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const params = {};
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;

      const response = await axios.get(`${API_URL}/api/reports/export/tasks`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `task_breakdown_${getFilterLabel().replace(/ /g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Task breakdown downloaded');
    } catch (error) {
      toast.error('Failed to download task report');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">View allocation vs actual hours by project and user</p>
        </div>
      </div>

      {/* Time Period Filter */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Time Period Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2 min-w-[180px]">
              <Label htmlFor="time-period">Period</Label>
              <Select value={timePeriod} onValueChange={setTimePeriod}>
                <SelectTrigger id="time-period" data-testid="time-period-select">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="specific_month">Specific Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {timePeriod === 'specific_month' && (
              <div className="space-y-2 min-w-[180px]">
                <Label htmlFor="specific-month">Month</Label>
                <Select value={specificMonth} onValueChange={setSpecificMonth}>
                  <SelectTrigger id="specific-month" data-testid="specific-month-select">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {timePeriod === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-[160px]"
                    data-testid="custom-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-[160px]"
                    data-testid="custom-end-date"
                  />
                </div>
              </>
            )}

            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
              <Calendar className="w-4 h-4" />
              <span>Showing: <strong className="text-foreground">{getFilterLabel()}</strong></span>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs defaultValue="projects" className="space-y-4">
          <TabsList>
            <TabsTrigger value="projects" className="gap-2" data-testid="projects-tab">
              <FolderKanban className="w-4 h-4" />
              By Project
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2" data-testid="users-tab">
              <Users className="w-4 h-4" />
              By User
            </TabsTrigger>
            <TabsTrigger value="tasks" className="gap-2" data-testid="tasks-tab">
              <ListTodo className="w-4 h-4" />
              Task Breakdown
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={downloadProjectCSV} variant="outline" data-testid="download-project-report">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {projectReport.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">No project data available for the selected period</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {projectReport.map((project) => (
                  <Card key={project.project_id} data-testid={`project-report-${project.project_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{project.project_name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{project.project_code}</p>
                        </div>
                        <StatusBadge status={project.status} />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{project.total_allocated}h</p>
                          <p className="text-xs text-muted-foreground">Allocated</p>
                        </div>
                        <div className="text-center p-4 bg-primary/10 rounded-lg">
                          <p className="text-2xl font-bold text-primary">{project.total_logged.toFixed(1)}h</p>
                          <p className="text-xs text-muted-foreground">Logged ({getFilterLabel()})</p>
                        </div>
                        <div className="text-center p-4 bg-muted/50 rounded-lg">
                          <p className={`text-2xl font-bold ${project.total_remaining < 0 ? 'text-red-600' : ''}`}>
                            {project.total_remaining.toFixed(1)}h
                          </p>
                          <p className="text-xs text-muted-foreground">Remaining</p>
                        </div>
                      </div>

                      <Accordion type="single" collapsible>
                        <AccordionItem value="users">
                          <AccordionTrigger>User Breakdown</AccordionTrigger>
                          <AccordionContent>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>User</TableHead>
                                  <TableHead className="text-right">Allocated</TableHead>
                                  <TableHead className="text-right">Logged</TableHead>
                                  <TableHead className="text-right">Remaining</TableHead>
                                  <TableHead>Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {project.users.map((user) => (
                                  <TableRow key={user.user_id}>
                                    <TableCell className="font-medium">{user.user_name}</TableCell>
                                    <TableCell className="text-right">{user.allocated_hours}h</TableCell>
                                    <TableCell className="text-right text-primary">{user.logged_hours.toFixed(1)}h</TableCell>
                                    <TableCell className={`text-right ${user.remaining_hours < 0 ? 'text-red-600' : ''}`}>
                                      {user.remaining_hours.toFixed(1)}h
                                    </TableCell>
                                    <TableCell>
                                      <StatusBadge status={user.status} showIcon={false} />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="users" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={downloadUserCSV} variant="outline" data-testid="download-user-report">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {userReport.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">No user data available for the selected period</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {userReport.map((user) => (
                  <Card key={user.user_id} data-testid={`user-report-${user.user_id}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{user.user_name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{user.user_email} • {(user.roles || [user.role]).join(', ')}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-muted/50 rounded-lg">
                          <p className="text-2xl font-bold">{user.total_allocated}h</p>
                          <p className="text-xs text-muted-foreground">Total Allocated</p>
                        </div>
                        <div className="text-center p-4 bg-primary/10 rounded-lg">
                          <p className="text-2xl font-bold text-primary">{user.total_logged.toFixed(1)}h</p>
                          <p className="text-xs text-muted-foreground">Logged ({getFilterLabel()})</p>
                        </div>
                        <div className="text-center p-4 bg-muted/50 rounded-lg">
                          <p className={`text-2xl font-bold ${user.total_remaining < 0 ? 'text-red-600' : ''}`}>
                            {user.total_remaining.toFixed(1)}h
                          </p>
                          <p className="text-xs text-muted-foreground">Total Remaining</p>
                        </div>
                      </div>

                      {user.projects.length > 0 && (
                        <Accordion type="single" collapsible>
                          <AccordionItem value="projects">
                            <AccordionTrigger>Project Breakdown</AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-2 pt-1">
                                {user.projects.map((project) => (
                                  <Accordion type="single" collapsible key={project.project_id}>
                                    <AccordionItem value={`daily-${project.project_id}`} className="border rounded-md">
                                      <AccordionTrigger className="px-4 py-3 hover:no-underline">
                                        <div className="flex items-center justify-between w-full pr-2 flex-wrap gap-2">
                                          <div className="text-left">
                                            <div className="font-medium">{project.project_name}</div>
                                            <div className="text-xs text-muted-foreground">{project.project_code}</div>
                                          </div>
                                          <div className="flex items-center gap-3 text-sm">
                                            <span className="text-muted-foreground">{project.allocated_hours}h alloc.</span>
                                            <span className="text-primary font-medium">{project.logged_hours.toFixed(1)}h logged</span>
                                            <span className={project.remaining_hours < 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>
                                              {project.remaining_hours.toFixed(1)}h rem.
                                            </span>
                                            <StatusBadge status={project.status} showIcon={false} />
                                          </div>
                                        </div>
                                      </AccordionTrigger>
                                      <AccordionContent className="px-4 pb-4">
                                        {(project.daily_entries || []).length === 0 ? (
                                          <p className="text-sm text-muted-foreground text-center py-4">No daily entries in this period</p>
                                        ) : (
                                          <Table>
                                            <TableHeader>
                                              <TableRow>
                                                <TableHead>Date</TableHead>
                                                <TableHead className="text-right">Hours</TableHead>
                                                <TableHead>Notes</TableHead>
                                                <TableHead>Status</TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {(project.daily_entries || []).map((entry) => (
                                                <TableRow key={entry.id}>
                                                  <TableCell>{format(new Date(entry.work_date + 'T00:00:00'), 'EEE, MMM d, yyyy')}</TableCell>
                                                  <TableCell className="text-right font-medium">{entry.hours}h</TableCell>
                                                  <TableCell className="text-muted-foreground">{entry.notes || '-'}</TableCell>
                                                  <TableCell><SubmissionStatusBadge status={entry.status} /></TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        )}
                                      </AccordionContent>
                                    </AccordionItem>
                                  </Accordion>
                                ))}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Task Breakdown Tab */}
          <TabsContent value="tasks" className="space-y-4">
            <div className="flex items-center justify-between">
              {/* View toggle */}
              <div className="flex rounded-md border overflow-hidden text-sm">
                <button
                  className={`px-3 py-1.5 transition-colors ${taskView === 'by-project' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
                  onClick={() => setTaskView('by-project')}
                >
                  By Project
                </button>
                <button
                  className={`px-3 py-1.5 transition-colors ${taskView === 'by-client' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
                  onClick={() => setTaskView('by-client')}
                >
                  By Client (Rollup)
                </button>
              </div>
              <Button onClick={downloadTaskCSV} variant="outline" data-testid="download-task-report">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* BY PROJECT view */}
            {taskView === 'by-project' && (
              taskReport.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">No task data available. Projects with tasks will appear here once time has been submitted.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {taskReport.map((project) => (
                    <Card key={project.project_id}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base">{project.project_name}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                              {project.project_code}
                              {project.client_name && <span className="ml-2 text-xs">· {project.client_name}</span>}
                            </p>
                          </div>
                          <span className="text-sm font-medium text-muted-foreground">
                            {project.tasks.reduce((s, t) => s + t.total_hours, 0).toFixed(1)}h total
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Task</TableHead>
                              <TableHead>Code</TableHead>
                              <TableHead className="text-right">Total Hours</TableHead>
                              <TableHead className="text-right">Entries</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {project.tasks.map((task) => (
                              <TableRow key={task.task_id}>
                                <TableCell className="font-medium">{task.task_name}</TableCell>
                                <TableCell className="text-muted-foreground">{task.task_code || '-'}</TableCell>
                                <TableCell className="text-right font-medium">{task.total_hours.toFixed(1)}h</TableCell>
                                <TableCell className="text-right text-muted-foreground">{task.entry_count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            )}

            {/* BY CLIENT rollup view */}
            {taskView === 'by-client' && (
              clientTaskRollup.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">No task data available. Projects with tasks will appear here once time has been submitted.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {clientTaskRollup.map((client) => (
                    <Card key={client.client_id}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base">{client.client_name}</CardTitle>
                            <p className="text-sm text-muted-foreground">
                              Across {client.project_count} project{client.project_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <span className="text-sm font-medium text-muted-foreground">
                            {client.tasks.reduce((s, t) => s + t.total_hours, 0).toFixed(1)}h total
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Task</TableHead>
                              <TableHead>Code</TableHead>
                              <TableHead className="text-right">Total Hours</TableHead>
                              <TableHead className="text-right">Entries</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {client.tasks.map((task) => (
                              <TableRow key={task.task_id}>
                                <TableCell className="font-medium">{task.task_name}</TableCell>
                                <TableCell className="text-muted-foreground">{task.task_code || '-'}</TableCell>
                                <TableCell className="text-right font-medium">{task.total_hours.toFixed(1)}h</TableCell>
                                <TableCell className="text-right text-muted-foreground">{task.entry_count}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
